import React, { useEffect, useState } from 'react';
import { UserCircle2, Target, Trophy, TrendingUp, Hourglass, Link2 } from 'lucide-react';
import { api, crore } from './api';
import { PaceAlerts, QuarterProgress } from './PaceAlerts';
import { AIPlanner } from './AIPlanner';

const Card = ({ id, label, value, sub, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`my-kpi-${id}`}>
    <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-500 uppercase">{label}</p><Icon className="w-4 h-4 text-slate-400" /></div>
    <p className="text-2xl font-bold text-slate-900 mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const RowsTable = ({ title, rows, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid={testId}>
    <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
    <table className="w-full text-sm min-w-[520px]">
      <thead><tr className="text-xs text-slate-500 uppercase">{['Name', 'Target', 'Won', 'Achv.', 'Expected', 'Pace', 'Gap'].map((h, i) => <th key={h} className={`px-2 py-1.5 ${i ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map(r => (
          <tr key={r.id || r.person_id}>
            <td className="px-2 py-2 font-medium">{r.name}</td>
            <td className="px-2 py-2 text-right tabular-nums">{crore(r.target)}</td>
            <td className="px-2 py-2 text-right tabular-nums text-green-700">{crore(r.achieved)}</td>
            <td className="px-2 py-2 text-right">{r.achievement_pct}%</td>
            <td className="px-2 py-2 text-right tabular-nums">{crore(r.expected)}</td>
            <td className="px-2 py-2 text-right">{r.pace_pct == null ? '-' : <span className={r.pace_pct >= 90 ? 'text-green-700' : 'text-red-600'}>{r.pace_pct}%</span>}</td>
            <td className="px-2 py-2 text-right tabular-nums text-red-600">{crore(r.gap)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const MyTargets = ({ fy, team }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api(`/my/${fy}`).then(setData).catch(() => setData({ linked: false })); }, [fy]);
  if (!data) return null;

  if (!data.linked) {
    return (
      <div className="space-y-6" data-testid="my-targets-unlinked">
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 flex gap-3">
          <Link2 className="w-5 h-5 shrink-0" />
          <p>Your login isn't linked to a salesperson yet. An admin can link it on <b>Sales &rarr; Sales Team</b> (edit the person &rarr; "App login"). Meanwhile you can see the full roadmap in the other tabs, or plan for any salesperson below.</p>
        </div>
        <AIPlanner fy={fy} people={team.filter(t => t.active)} />
      </div>
    );
  }

  const me = data.me;
  const isLead = me.role === 'team_lead';
  return (
    <div className="space-y-6" data-testid="my-targets">
      <div className="flex items-center gap-3">
        <UserCircle2 className="w-10 h-10 text-slate-300" />
        <div>
          <p className="text-lg font-semibold text-slate-900" data-testid="my-name">{me.name}</p>
          <p className="text-xs text-slate-500">{isLead ? 'Team Lead' : 'Salesperson'} • {data.business_units.map(b => b.code || b.name).join(', ') || 'No business units'}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card id="target" label="My FY Target" value={crore(me.target)} icon={Target} />
        <Card id="won" label="Won" value={crore(me.achieved)} sub={`${me.achievement_pct}% achieved`} icon={Trophy} />
        <Card id="gap" label="Gap" value={crore(me.gap)} icon={TrendingUp} />
        <Card id="expected" label="Expected by Today" value={crore(me.expected)} sub={`Pace ${me.pace_pct ?? '-'}%`} icon={Target} />
        <Card id="pipeline" label="Open Pipeline" value={crore(me.pipeline)} icon={Hourglass} />
      </div>
      <QuarterProgress row={me} title="My quarterly progress" testId="my-quarters" />
      <PaceAlerts alerts={data.alerts} fyProgress={data.fy_progress_pct} testId="my-alerts" />
      <RowsTable title={isLead ? 'Business units I lead' : 'My business units'} rows={data.business_units} testId="my-bus" />
      {isLead && data.team.length > 0 && <RowsTable title="My team" rows={data.team} testId="my-team" />}
      <AIPlanner fy={fy} people={isLead ? [me, ...data.team] : []} defaultPersonId={isLead ? me.id : ''} />
    </div>
  );
};
